import { CheckCircle2, ArrowRight, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { SectionHead } from "./SectionHead";
import { Sparkle } from "./Sparkle";
import { TypeLine } from "./CaseStory";

const list = (v) => (Array.isArray(v) ? v : []);
const fmt = (n) => n.toLocaleString("en-GB");
const Paras = ({ items, className = "text-slate-600" }) => list(items).map((p) => <p key={p} className={`text-base leading-relaxed md:text-lg ${className}`}>{p}</p>);

const SrTable = ({ chart }) => (
  <table className="sr-only">
    <caption>{chart.axis || "Share of estimated organic traffic"}</caption>
    <tbody>{list(chart.bars || chart.segments).map((b) => <tr key={b.label}><th scope="row">{b.label}</th><td>{chart.segments ? `${b.value}%` : fmt(b.value)}</td></tr>)}</tbody>
  </table>
);

const Columns = ({ chart }) => {
  const max = Math.max(...list(chart.bars).map((b) => b.value));
  return (
    <div>
      <p className="text-3xl font-extrabold text-[#581C87] sm:text-4xl" data-testid="chart-headline"><Sparkle>{chart.headline}</Sparkle></p>
      <p className="mt-1 text-xs font-semibold uppercase tracking-widest text-slate-600">{chart.axis}</p>
      <div className="mt-6 flex h-56 items-end gap-6 border-b border-violet-200 sm:gap-10" aria-hidden="true">
        {list(chart.bars).map((b, i) => (
          <div key={b.label} className="flex h-full flex-1 flex-col justify-end">
            <span className="mb-2 text-center text-lg font-extrabold text-[#13052A] sm:text-xl">{fmt(b.value)}</span>
            <motion.div initial={{ height: "8%" }} whileInView={{ height: `${(b.value / max) * 78}%` }} viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 1.2, delay: i * 0.2, ease: [0.22, 1, 0.36, 1] }}
              className={`rounded-t-xl ${i === list(chart.bars).length - 1 ? "bg-gradient-to-t from-[#581C87] to-[#7C3AED]" : "bg-violet-200"}`} />
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-6 sm:gap-10" aria-hidden="true">{list(chart.bars).map((b) => <span key={b.label} className="flex-1 text-center text-sm font-semibold text-slate-700">{b.label}</span>)}</div>
      {chart.label && <p className="mt-4 text-sm font-semibold text-[#13052A]">{chart.label}</p>}
      {list(chart.stats).length > 0 && (
        <dl className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="chart-stats">
          {chart.stats.map((s) => (
            <div key={s.label} className="rounded-2xl bg-[#F5F0FF] p-4">
              <dt className="text-xs font-bold uppercase tracking-widest text-[#581C87]">{s.label}</dt>
              <dd className="mt-1 flex items-center gap-2 text-lg font-extrabold text-[#13052A]">{s.before} <ArrowRight className="h-4 w-4 text-[#7C3AED]" aria-label="to" /> {s.after}</dd>
              <dd className="text-xs text-slate-600">Previous 6 months to latest 6 months</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};

const Bars = ({ chart }) => {
  const max = Math.max(...list(chart.bars).map((b) => b.value));
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-600">{chart.axis}</p>
      <ul className={`mt-4 ${chart.compact ? "space-y-4" : "space-y-3"}`} aria-hidden="true">
        {list(chart.bars).map((b, i) => (
          <li key={b.label}>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="font-semibold text-[#13052A]">{b.label}</span>
              <span className="font-extrabold text-[#13052A]">{fmt(b.value)}</span>
            </div>
            <div className="mt-1.5 h-3 rounded-full bg-violet-100">
              <div className={`h-3 rounded-full ${i === 0 ? "bg-[#581C87]" : "bg-[#7C3AED]"}`} style={{ width: `${(b.value / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
      {chart.total && <p className="mt-6 text-base font-extrabold text-[#581C87]" data-testid="chart-total">{chart.total}</p>}
    </div>
  );
};

const Donut = ({ chart }) => {
  const [a, b] = list(chart.segments);
  const r = 70; const len = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center gap-6 sm:flex-row sm:gap-10">
      <svg viewBox="0 0 180 180" className="h-48 w-48 shrink-0" aria-hidden="true">
        <circle cx="90" cy="90" r={r} fill="none" stroke="#DDD6FE" strokeWidth="26" />
        <circle cx="90" cy="90" r={r} fill="none" stroke="#581C87" strokeWidth="26" strokeDasharray={`${(a.value / 100) * len} ${len}`} transform="rotate(-90 90 90)" />
        <text x="90" y="88" textAnchor="middle" className="fill-[#13052A] text-[26px] font-extrabold">{a.value}%</text>
        <text x="90" y="110" textAnchor="middle" className="fill-slate-600 text-[12px] font-semibold">{a.label}</text>
      </svg>
      <ul className="space-y-3">
        {[a, b].map((s, i) => (
          <li key={s.label} className="flex items-center gap-3">
            <span className={`h-4 w-4 rounded ${i === 0 ? "bg-[#581C87]" : "bg-[#DDD6FE] ring-1 ring-violet-300"}`} />
            <span className={`${i === 0 ? "text-2xl" : "text-lg"} font-extrabold text-[#13052A]`}>{s.value}%</span>
            <span className="text-sm font-semibold text-slate-600">{s.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const CHARTS = { columns: Columns, bars: Bars, donut: Donut };

const Chart = ({ r }) => {
  const C = CHARTS[r.chart?.type];
  if (!C) return null;
  return (
    <figure className="rounded-[28px] border border-violet-100 bg-white p-6 sm:p-8" data-testid={`chart-${r.id}`}>
      <C chart={r.chart} />
      <SrTable chart={r.chart} />
      <figcaption className="mt-6 border-t border-violet-100 pt-4 text-xs font-semibold text-slate-600" data-testid={`chart-source-${r.id}`}>Source: {r.source}</figcaption>
    </figure>
  );
};

export const SeoChallenge = ({ c }) => (
  <section className="bg-white pb-20 pt-24 sm:pb-28 sm:pt-28" data-testid="cs-challenge">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_1.3fr]">
      <SectionHead eyebrow="The challenge" title={c.challenge_title} />
      <Reveal className="space-y-4">
        <Paras items={c.challenge} />
        <ul className="flex flex-wrap gap-2" data-testid="cs-target-searches">
          {list(c.target_searches).map((s) => <li key={s} className="inline-flex items-center gap-1.5 rounded-full bg-[#F5F0FF] px-3.5 py-2 text-sm font-semibold text-[#13052A]"><MapPin className="h-3.5 w-3.5 text-[#7C3AED]" aria-hidden="true" />{s}</li>)}
        </ul>
        <Paras items={c.challenge_close} />
      </Reveal>
    </div>
  </section>
);

export const SeoStrategy = ({ c }) => (
  <section className="bg-[#F5F0FF] py-20 sm:py-28" data-testid="cs-strategy">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead eyebrow="Our SEO strategy" title={c.strategy_title} text={c.strategy_intro} />
      <ol className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {list(c.strategy).map((s, i) => (
          <Reveal key={s.title} delay={(i % 3) * 0.05} y={14} className="h-full">
            <li className="h-full rounded-[22px] bg-white p-6" data-testid={`cs-strategy-${i + 1}`}>
              <span className="text-xs font-bold uppercase tracking-widest text-[#7C3AED]">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-2 text-lg font-extrabold text-[#13052A]">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.text}</p>
            </li>
          </Reveal>
        ))}
      </ol>
      {c.initiative && (
        <Reveal className="mt-10 rounded-[28px] bg-[#13052A] p-7 text-white sm:p-10" data-testid="cs-initiative">
          <h3 className="text-2xl font-extrabold leading-tight sm:text-3xl">{c.initiative.title}</h3>
          <div className="mt-5 grid gap-4 lg:grid-cols-2"><Paras items={c.initiative.paras} className="text-violet-100" /></div>
        </Reveal>
      )}
    </div>
  </section>
);

export const SeoResult = ({ r, i }) => (
  <section id={r.id} className={`scroll-mt-24 py-20 sm:py-24 ${i % 2 ? "bg-[#F5F0FF]" : "bg-white"}`} data-testid={`cs-seo-result-${i + 1}`}>
    <div className="mx-auto grid max-w-7xl items-start gap-10 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16">
      <div>
        <SectionHead eyebrow={r.eyebrow} title={r.title} />
        <Reveal className="mt-6 space-y-4">
          <Paras items={r.paras} />
          {list(r.notes).map((n) => <p key={n} className="border-l-2 border-[#7C3AED] pl-4 text-sm leading-relaxed text-slate-600">{n}</p>)}
        </Reveal>
      </div>
      <Reveal delay={0.08}><Chart r={r} /></Reveal>
    </div>
  </section>
);

export const SeoEnquiries = ({ c }) => {
  const e = c.enquiries || {};
  return (
    <section className="grain relative overflow-hidden bg-[#13052A] py-20 text-white sm:py-28" data-testid="cs-enquiries">
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr]">
        <Reveal>
          <p className="eyebrow text-violet-200"><span className="h-1.5 w-1.5 rounded-full bg-[#FF7043]" />Business outcome · Membership enquiries</p>
          <p className="mt-6 text-[5.5rem] font-extrabold leading-none tracking-tight text-[#FF7043] sm:text-[8rem]" data-testid="cs-enquiries-figure"><TypeLine text="2–5" delay={300} className="" /></p>
          <p className="mt-2 text-lg font-bold text-violet-100">membership enquiries each month from organic search</p>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="text-3xl font-extrabold leading-tight sm:text-4xl">{e.title}</h2>
          <div className="mt-5 space-y-4"><Paras items={e.paras} className="text-violet-100" /></div>
          <ol className="mt-8 flex flex-wrap items-center gap-2" data-testid="cs-enquiries-chain">
            {list(e.chain).map((s, i) => (
              <li key={s} className="flex items-center gap-2">
                <span className={`rounded-full px-3.5 py-2 text-sm font-bold ${i === list(e.chain).length - 1 ? "bg-[#FF7043] text-[#13052A]" : "bg-white/10 text-white"}`}>{s}</span>
                {i < list(e.chain).length - 1 && <ArrowRight className="h-4 w-4 text-violet-200" aria-hidden="true" />}
              </li>
            ))}
          </ol>
          <p className="mt-6 text-sm text-violet-200" data-testid="cs-enquiries-note">{e.note}</p>
        </Reveal>
      </div>
    </section>
  );
};

export const SeoOutcome = ({ c }) => (
  <section className="bg-white py-20 sm:py-28" data-testid="cs-outcome">
    <div className="mx-auto max-w-4xl px-5 sm:px-8">
      <SectionHead eyebrow="The outcome" title={c.outcome_title} />
      <Reveal className="mt-6 space-y-4"><Paras items={c.outcome} /></Reveal>
      <p className="mt-10 flex items-start gap-3 rounded-2xl bg-[#F5F0FF] p-5 text-sm leading-relaxed text-slate-700" data-testid="cs-results-note">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#7C3AED]" aria-hidden="true" />{c.results_note}
      </p>
    </div>
  </section>
);
